import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import MediaCard from './MediaCard';
import { Density, DENSITY_CELL } from './mediaCardUtils';

export interface MediaGridProps {
  items: MediaLibraryItem[];
  selectedIds: string[];
  selectMode: boolean;
  density: Density;
  loading: boolean;
  togglingFavouriteId: string | null;
  /** Whether the list has search/filter applied (drives zero-state copy). */
  hasActiveFilters?: boolean;
  errorMessage?: string;
  /** Mono terminus rule under the last item (e.g. "· END · 412 items ·"). */
  endLabel?: string;
  onActivate: (mediaId: string) => void;
  onToggleSelect: (mediaId: string) => void;
  onToggleFavourite: (mediaId: string, current: boolean) => void;
  /** shift = range-select from anchor; cmd/ctrl = additive toggle. */
  onRangeSelect?: (mediaIds: string[]) => void;
  onAdditiveToggle?: (mediaId: string) => void;
  onClearFilters?: () => void;
  onRetry?: () => void;
  /** Hide per-card favourite affordance (single-select picker). Default true. */
  showFavourite?: boolean;
}

const SKELETON_COUNT = 12;

/**
 * The contact-sheet grid. Renders unified MediaCards in a responsive,
 * density-driven CSS grid, with branded skeleton / empty / zero-result / error
 * states. Shift-click range selection is resolved here against the rendered
 * order (the only place that knows the visible sequence).
 */
const MediaGrid: React.FC<MediaGridProps> = ({
  items,
  selectedIds,
  selectMode,
  density,
  loading,
  togglingFavouriteId,
  hasActiveFilters = false,
  errorMessage,
  endLabel,
  onActivate,
  onToggleSelect,
  onToggleFavourite,
  onRangeSelect,
  onAdditiveToggle,
  onClearFilters,
  onRetry,
  showFavourite = true,
}) => {
  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  const anchorRef = React.useRef<string | null>(null);

  const cellVar = { ['--media-cell' as string]: `${DENSITY_CELL[density]}px` } as React.CSSProperties;

  const handleToggleSelect = React.useCallback(
    (mediaId: string) => {
      anchorRef.current = mediaId;
      onToggleSelect(mediaId);
    },
    [onToggleSelect]
  );

  const handleModifierClick = React.useCallback(
    (mediaId: string, e: React.MouseEvent | React.KeyboardEvent) => {
      if ('shiftKey' in e && e.shiftKey && onRangeSelect && anchorRef.current) {
        const ids = items.map((it) => it.media_id || it.id);
        const from = ids.indexOf(anchorRef.current);
        const to = ids.indexOf(mediaId);
        if (from !== -1 && to !== -1) {
          const [lo, hi] = from < to ? [from, to] : [to, from];
          onRangeSelect(ids.slice(lo, hi + 1));
          return;
        }
      }
      if ('shiftKey' in e && (e.metaKey || e.ctrlKey) && onAdditiveToggle) {
        anchorRef.current = mediaId;
        onAdditiveToggle(mediaId);
        return;
      }
      // No effective modifier — fall back to plain activate.
      anchorRef.current = mediaId;
      onActivate(mediaId);
    },
    [items, onRangeSelect, onAdditiveToggle, onActivate]
  );

  // ── Error state ──────────────────────────────────────────────────────────
  if (errorMessage && items.length === 0) {
    return (
      <div className="media-grid" style={cellVar} role="grid" aria-label="Media library">
        <div className="media-state-card">
          <p className="media-state-title">Couldn't load media</p>
          <p>{errorMessage}</p>
          {onRetry && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Skeleton (cold load) ───────────────────────────────────────────────────
  if (loading && items.length === 0) {
    return (
      <div className="media-grid" style={cellVar} aria-busy="true" aria-label="Loading media">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <div key={i} className="media-skeleton pipeline-progress" />
        ))}
      </div>
    );
  }

  // ── Empty / zero-result ────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className="media-grid" style={cellVar} role="grid" aria-label="Media library">
        <div className="media-state-card">
          {hasActiveFilters ? (
            <>
              <p className="media-state-title">No matches</p>
              <p>No media matches the current search and filters.</p>
              {onClearFilters && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={onClearFilters}>
                  Clear filters
                </button>
              )}
            </>
          ) : (
            <>
              <p className="media-state-title">No media yet</p>
              <p>Upload or generate media and it will appear here.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Grid ───────────────────────────────────────────────────────────────────
  return (
    <div
      className="media-grid motion-stagger"
      style={cellVar}
      role="grid"
      aria-label="Media library"
    >
      {items.map((item) => {
        const mediaId = item.media_id || item.id;
        return (
          <div role="gridcell" aria-selected={selectedSet.has(mediaId)} key={mediaId}>
            <MediaCard
              item={item}
              selected={selectedSet.has(mediaId)}
              selectMode={selectMode}
              togglingFavourite={togglingFavouriteId === mediaId}
              onActivate={onActivate}
              onToggleSelect={handleToggleSelect}
              onToggleFavourite={onToggleFavourite}
              onClickWithModifiers={onRangeSelect || onAdditiveToggle ? handleModifierClick : undefined}
              showFavourite={showFavourite}
            />
          </div>
        );
      })}
      {endLabel && <div className="media-end-rule">{endLabel}</div>}
    </div>
  );
};

export default MediaGrid;
